import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as kbApi from '../../api/knowledgeBase';
import { formatDate } from '../../utils/format';

const CATEGORY_OPTIONS = ['SOP Documents', 'Audit Procedures', 'Internal Policies', 'Training Materials'].map((v) => ({ value: v, label: v }));

export default function KnowledgeBase() {
  const { actor } = useAuth();
  const canManage = actor.role === 'super_admin' || actor.role === 'admin';

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'SOP Documents', file: null });

  const load = useCallback(() => {
    setLoading(true);
    kbApi
      .listArticles({ search: search || undefined, category: category || undefined })
      .then((res) => setRows(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load articles.'))
      .finally(() => setLoading(false));
  }, [search, category]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await kbApi.createArticle(form);
      setSuccess('Article published.');
      setShowForm(false);
      setForm({ title: '', content: '', category: 'SOP Documents', file: null });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to publish article.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await kbApi.deleteArticle(row.id);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete article.');
    }
  };

  return (
    <DashboardLayout title="Knowledge Base">
      <div className="page-header">
        <div>
          <h1 className="heading">Knowledge Base</h1>
          <p className="page-subtext">SOPs, audit procedures, policies and training materials.</p>
        </div>
        {canManage && <button type="button" className="btn" onClick={() => setShowForm(true)}>New Article</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      <div className="filter-bar">
        <input type="text" placeholder="Search articles" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      {loading ? (
        <Loader text="Loading articles..." />
      ) : rows.length === 0 ? (
        <p className="text-muted">No articles found.</p>
      ) : (
        rows.map((a) => (
          <div key={a.id} className="card">
            <div className="flex-between">
              <h3 className="subheading mb-0">{a.title}</h3>
              {canManage && <button type="button" className="btn-link" onClick={() => handleDelete(a)}>Delete</button>}
            </div>
            <div className="text-muted" style={{ fontSize: '9.5pt', margin: '4px 0 10px' }}>
              {a.category} &middot; Updated {formatDate(a.updatedAt)}
            </div>
            <p>{a.content}</p>
            {a.filePath && <button type="button" className="btn-link" onClick={() => kbApi.downloadArticle(a.id, a.filePath)}>Download Attachment</button>}
          </div>
        ))
      )}

      {showForm && (
        <Modal
          title="New Article"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="kb-form" className="btn">Publish</button>
            </>
          }
        >
          <form id="kb-form" onSubmit={handleCreate}>
            <div className="form-grid single-column">
              <FormField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              <FormField label="Category" type="select" options={CATEGORY_OPTIONS} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <FormField label="Content" type="textarea" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
              <FormField label="Attachment (optional)" type="file" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} />
            </div>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}